/**
 * AnagrafePage — Console operatore /anagrafe
 *
 * La CURA dell'anagrafe comune dei componenti (`catalog_components`, capitoli 66 e 162):
 * un modello del mercato = UNA voce, con marca, codice, specifiche e i media
 * universali (logo, texture, modello 3D, schede). I tenant non scrivono mai qui:
 * questa pagina è l'unico posto, ed è del super utente.
 *
 * Dall'ondata zero del magazzino la pagina ha due schede: i COMPONENTI e il registro dei
 * MARCHI (un marchio vero = una voce, con gli alias). Il prezzo NON compare, per scelta:
 * resta sul prodotto del tenant.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import TextureOutlined from '@mui/icons-material/TextureOutlined';
import ViewInAr from '@mui/icons-material/ViewInAr';
import { SoftChip } from '../components/SoftChip';
import SchedaComponente from '../components/anagrafe/SchedaComponente';
import SchedaMarchio from '../components/anagrafe/SchedaMarchio';
import { caricaAnagrafe, caricaMarchi } from '../services/anagrafeService';
import type { ComponenteCanonico, Marchio } from '../types/anagrafe';

/** Filtro marchio: valori speciali oltre agli id del registro. */
const FILTRO_TUTTI = 'all';
const FILTRO_DA_ASSEGNARE = '__da_assegnare';
const FILTRO_SENZA = '__senza';

/** I «timbri» dei media presenti, come icone mute: si vedono a colpo d'occhio in lista. */
const TimbriMedia: React.FC<{ c: ComponenteCanonico }> = ({ c }) => {
  const has = (tipo: string) => (c.media ?? []).some((m) => m.tipo === tipo);
  const timbro = (presente: boolean, titolo: string, icona: React.ReactNode) => (
    <Tooltip title={`${titolo}: ${presente ? 'presente' : 'assente'}`}>
      <Box component="span" sx={{ color: presente ? 'primary.main' : 'grey.300', display: 'inline-flex' }}>
        {icona}
      </Box>
    </Tooltip>
  );
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      {timbro(has('modello_3d'), 'Modello 3D', <ViewInAr sx={{ fontSize: 18 }} />)}
      {timbro(has('texture'), 'Texture', <TextureOutlined sx={{ fontSize: 18 }} />)}
      {timbro(
        (c.media ?? []).some((m) => !['modello_3d', 'texture'].includes(m.tipo)),
        'Schede e documenti',
        <DescriptionOutlined sx={{ fontSize: 18 }} />,
      )}
    </Stack>
  );
};

/** La cella «Marchio»: il nome del registro, oppure il segnale di cosa manca. */
const CellaMarchio: React.FC<{ c: ComponenteCanonico; marchi: Map<string, Marchio> }> = ({ c, marchi }) => {
  if (c.marchio_da_assegnare) return <SoftChip label="da assegnare" tone="warning" />;
  if (!c.marchio_id) return <Typography variant="caption" color="text.secondary">senza marca</Typography>;
  const m = marchi.get(c.marchio_id);
  return <Typography variant="body2">{m?.nome_canonico ?? c.marchio_id}</Typography>;
};

export const AnagrafePage: React.FC = () => {
  const [scheda, setScheda] = useState<'componenti' | 'marchi'>('componenti');
  const [componenti, setComponenti] = useState<ComponenteCanonico[] | null>(null);
  const [marchi, setMarchi] = useState<Marchio[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [ricerca, setRicerca] = useState('');
  const [categoria, setCategoria] = useState(FILTRO_TUTTI);
  const [filtroMarchio, setFiltroMarchio] = useState(FILTRO_TUTTI);
  const [apertoId, setApertoId] = useState<string | null>(null);
  const [marchioApertoId, setMarchioApertoId] = useState<string | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({ open: false, severity: 'success', message: '' });

  const carica = async () => {
    setComponenti(null);
    setErrore(null);
    try {
      setComponenti(await caricaAnagrafe());
    } catch (e) {
      console.error('[anagrafe] caricamento componenti fallito:', e);
      setErrore(e instanceof Error ? e.message : 'Caricamento fallito');
      setComponenti([]);
      return;
    }
    // Il registro dei marchi è una collezione a sé: se non si legge (regole non ancora pubblicate)
    // la pagina lo DICE e mostra i componenti lo stesso, invece di cadere tutta intera.
    try {
      setMarchi(await caricaMarchi());
    } catch (e) {
      console.error('[anagrafe] caricamento marchi fallito:', e);
      setMarchi([]);
      setErrore(`Il registro dei marchi non si legge (${e instanceof Error ? e.message : 'errore'}): controlla che le regole per «marchi» siano pubblicate.`);
    }
  };
  useEffect(() => { carica(); }, []);

  const marchiPerId = useMemo(() => new Map(marchi.map((m) => [m.id, m])), [marchi]);

  const categorie = useMemo(
    () => [...new Set((componenti ?? []).map((c) => c.categoria_code).filter(Boolean))].sort(),
    [componenti],
  );

  const attivi = useMemo(() => (componenti ?? []).filter((c) => c.attivo), [componenti]);

  const filtrati = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return (componenti ?? []).filter((c) => {
      if (categoria !== FILTRO_TUTTI && c.categoria_code !== categoria) return false;
      if (filtroMarchio === FILTRO_DA_ASSEGNARE && !c.marchio_da_assegnare) return false;
      if (filtroMarchio === FILTRO_SENZA && (c.marchio_id || c.marchio_da_assegnare)) return false;
      if (filtroMarchio !== FILTRO_TUTTI && filtroMarchio !== FILTRO_DA_ASSEGNARE && filtroMarchio !== FILTRO_SENZA && c.marchio_id !== filtroMarchio) return false;
      if (!q) return true;
      return [c.costruttore, c.modello, c.codice_costruttore, c.mpn_normalizzato, c.id, ...c.gtin]
        .some((t) => (t ?? '').toLowerCase().includes(q));
    });
  }, [componenti, ricerca, categoria, filtroMarchio]);

  const aperto = useMemo(
    () => (apertoId ? (componenti ?? []).find((c) => c.id === apertoId) ?? null : null),
    [componenti, apertoId],
  );
  const marchioAperto = useMemo(
    () => (marchioApertoId ? marchi.find((m) => m.id === marchioApertoId) ?? null : null),
    [marchi, marchioApertoId],
  );

  const componentiPerMarchio = useMemo(() => {
    const conta = new Map<string, number>();
    for (const c of attivi) if (c.marchio_id) conta.set(c.marchio_id, (conta.get(c.marchio_id) ?? 0) + 1);
    return conta;
  }, [attivi]);

  const conMedia = attivi.filter((c) => c.logo_url || (c.media ?? []).length > 0).length;
  const daAssegnare = attivi.filter((c) => c.marchio_da_assegnare).length;
  const senzaMarca = attivi.filter((c) => !c.marchio_id && !c.marchio_da_assegnare).length;
  const proposti = attivi.filter((c) => c.stato_curatela === 'proposto').length;

  const marchiFiltrati = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    return marchi.filter((m) => !q || m.nome_canonico.toLowerCase().includes(q) || m.alias.some((a) => a.toLowerCase().includes(q)));
  }, [marchi, ricerca]);

  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Anagrafe componenti</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
            Un modello del mercato = una voce: marchio, codice del costruttore, come si riconosce
            sullo scaffale, specifiche e media universali. I cataloghi dei tenant ci vestono sopra
            il commerciale — il prezzo non abita qui.
          </Typography>
        </Box>
        <Tooltip title="Ricarica dall'anagrafe">
          <IconButton onClick={carica} sx={{ width: 40, height: 40 }}><Refresh /></IconButton>
        </Tooltip>
      </Stack>

      {componenti !== null && (
        <Stack direction="row" spacing={1} sx={{ my: 1.5, flexWrap: 'wrap', gap: 0.75 }}>
          <SoftChip label={`${attivi.length} componenti`} tone="primary" />
          <SoftChip label={`${marchi.filter((m) => m.attivo).length} marchi`} tone="info" />
          {daAssegnare > 0 && <SoftChip label={`${daAssegnare} con marchio da assegnare`} tone="warning" />}
          {senzaMarca > 0 && <SoftChip label={`${senzaMarca} senza marca`} tone="default" />}
          {proposti > 0 && <SoftChip label={`${proposti} proposti dai tenant`} tone="gold" />}
          <SoftChip label={`${conMedia} con media universali`} tone="default" />
        </Stack>
      )}

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      <Tabs value={scheda} onChange={(_, v) => setScheda(v)} sx={{ mb: 2, '& .MuiTab-root': { minHeight: 44, textTransform: 'none' } }}>
        <Tab value="componenti" label="Componenti" />
        <Tab value="marchi" label="Marchi" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            size="small"
            placeholder={scheda === 'componenti' ? 'cerca per marchio, modello, codice o codice a barre' : 'cerca un marchio o una sua scrittura'}
            value={ricerca}
            onChange={(e) => setRicerca(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          />
          {scheda === 'componenti' && (
            <>
              <TextField size="small" select label="categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)} sx={{ minWidth: 220 }}>
                <MenuItem value={FILTRO_TUTTI}>tutte le categorie</MenuItem>
                {categorie.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="marchio" value={filtroMarchio} onChange={(e) => setFiltroMarchio(e.target.value)} sx={{ minWidth: 220 }}>
                <MenuItem value={FILTRO_TUTTI}>tutti i marchi</MenuItem>
                <MenuItem value={FILTRO_DA_ASSEGNARE}>da assegnare</MenuItem>
                <MenuItem value={FILTRO_SENZA}>senza marca</MenuItem>
                {marchi.map((m) => <MenuItem key={m.id} value={m.id}>{m.nome_canonico}</MenuItem>)}
              </TextField>
            </>
          )}
        </Stack>
      </Paper>

      {componenti === null ? (
        <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>
      ) : scheda === 'componenti' ? (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Componente</TableCell>
                <TableCell>Marchio</TableCell>
                <TableCell>Codice</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell align="center">Prodotti</TableCell>
                <TableCell>Media</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtrati.map((c) => (
                <TableRow
                  key={c.id}
                  hover
                  onClick={() => setApertoId(c.id)}
                  sx={{ cursor: 'pointer', opacity: c.attivo ? 1 : 0.55, '& td': { py: 1 } }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={c.logo_url} variant="rounded" sx={{ width: 30, height: 30, bgcolor: 'grey.100', color: 'grey.500', fontSize: 13 }}>
                        {(c.costruttore ?? '?').slice(0, 1)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 380 }}>{c.modello ?? c.id}</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" color="text.secondary">{c.costruttore ?? 'costruttore da assegnare'}</Typography>
                          {c.gtin.length > 0 && <SoftChip label={`${c.gtin.length} codici a barre`} tone="info" />}
                          {c.stato_curatela === 'proposto' && <SoftChip label="proposto" tone="gold" />}
                          {!c.attivo && <SoftChip label={c.fuso_in ? 'fuso' : 'spento'} tone="error" />}
                        </Stack>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell><CellaMarchio c={c} marchi={marchiPerId} /></TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'var(--f-mono)' }}>{c.codice_costruttore ?? '—'}</Typography>
                  </TableCell>
                  <TableCell><SoftChip label={c.categoria_code || '—'} tone="default" /></TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{c.origine_prodotti.length}</Typography>
                  </TableCell>
                  <TableCell><TimbriMedia c={c} /></TableCell>
                </TableRow>
              ))}
              {filtrati.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      Nessun componente corrisponde alla ricerca.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Marchio</TableCell>
                <TableCell>Scritture riconosciute</TableCell>
                <TableCell align="center">Componenti</TableCell>
                <TableCell>Stato</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marchiFiltrati.map((m) => (
                <TableRow key={m.id} hover onClick={() => setMarchioApertoId(m.id)} sx={{ cursor: 'pointer', '& td': { py: 1 } }}>
                  <TableCell>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={m.logo_url ?? undefined} variant="rounded" sx={{ width: 30, height: 30, bgcolor: 'grey.100', color: 'grey.500', fontSize: 13 }}>
                        {m.nome_canonico.slice(0, 1)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">{m.nome_canonico}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'var(--f-mono)' }}>{m.id}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                      {m.alias.slice(0, 4).map((a) => <SoftChip key={a} label={a} tone="default" mono />)}
                      {m.alias.length > 4 && <Typography variant="caption" color="text.secondary">+{m.alias.length - 4}</Typography>}
                    </Stack>
                  </TableCell>
                  <TableCell align="center"><Typography variant="body2">{componentiPerMarchio.get(m.id) ?? 0}</Typography></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <SoftChip label={m.origine === 'manuale' ? 'curato a mano' : 'dalla corsa'} tone="default" />
                      {!m.attivo && <SoftChip label="spento" tone="warning" />}
                      {m.formato_matricola && <SoftChip label="forma matricola" tone="info" />}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {marchiFiltrati.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      Nessun marchio corrisponde alla ricerca.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <SchedaComponente
        componente={aperto}
        marchi={marchi}
        onChiudi={() => setApertoId(null)}
        onAggiornato={(agg) => setComponenti((prev) => (prev ?? []).map((c) => (c.id === agg.id ? agg : c)))}
        onEsito={(severity, message) => setSnack({ open: true, severity, message })}
      />

      <SchedaMarchio
        marchio={marchioAperto}
        componentiCollegati={marchioAperto ? componentiPerMarchio.get(marchioAperto.id) ?? 0 : 0}
        onChiudi={() => setMarchioApertoId(null)}
        onAggiornato={(m, rinominati) => {
          setMarchi((prev) => prev.map((x) => (x.id === m.id ? m : x)));
          // Il nome canonico è la cache di `costruttore` sui componenti collegati: si allinea in lista.
          if (rinominati > 0) setComponenti((prev) => (prev ?? []).map((c) => (c.marchio_id === m.id ? { ...c, costruttore: m.nome_canonico } : c)));
        }}
        onEsito={(severity, message) => setSnack({ open: true, severity, message })}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AnagrafePage;
